import { get } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { z } from "zod";

const WORKER_TRIPS_REFETCH_INTERVAL_MS = 60_000;

const workerMovementSchema = z.enum(["MOVING", "STATIONARY", "UNKNOWN"]);

const tripStatusSchema = z.enum([
  "TRIP_PLACEHOLDER",
  "TRIP_PENDING",
  "TRIP_STARTED",
  "TRIP_APPROACHING",
  "TRIP_ARRIVED",
  "TRIP_COMPLETED",
  "TRIP_CANCELLED",
  "TRIP_EXPIRED",
]);

const tripAttributesSchema = z.object({
  isLate: z.boolean(),
  workerId: z.string(),
  workplaceId: z.string(),
  status: tripStatusSchema,
  currentLatitude: z.number().optional(),
  currentLongitude: z.number().optional(),
  currentEtaDuration: z.number(),
  currentEtaDistance: z.number(),
  initialEtaDuration: z.number(),
  initialEtaDistance: z.number(),
  latestWorkerMovement: workerMovementSchema.optional(),
  shiftId: z.string(),
  locationProviderTripId: z.string().optional(),
  tripStartTime: z.string().optional(),
  tripStopTime: z.string().optional(),
  lastLocationReceivedByServerAt: z.string().optional(),
  latestWorkerMovementTransitionedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  workerDeclaredEta: z.string().optional(),
  trackingUrl: z.string().url().optional(),
});

const tripResponseItemSchema = z.object({
  id: z.string(),
  type: z.literal("trip"),
  attributes: tripAttributesSchema,
});

const tripsResponseSchema = z.array(tripResponseItemSchema);

type TripResponseItem = z.infer<typeof tripResponseItemSchema>;

export type TripStatus = z.infer<typeof tripStatusSchema>;
export type WorkerMovement = z.infer<typeof workerMovementSchema>;

export interface WorkerTripData {
  shiftId: string;
  workerId: string;
  isLate: boolean;
  status: TripStatus;
  /** Current ETA duration in minutes */
  currentEtaDuration: number;
  /** Current ETA distance in meters */
  currentEtaDistance: number;
  latestWorkerMovement?: WorkerMovement;
  /** ISO timestamp of when the movement state last changed (MOVING/STATIONARY/UNKNOWN) */
  latestWorkerMovementTransitionedAt?: string;
  /** ISO timestamp of when the server last received a location update from the worker's device */
  lastLocationReceivedByServerAt?: string;
  workerDeclaredEta?: string;
  /** Worker's current latitude (from live tracking) */
  currentLatitude?: number;
  /** Worker's current longitude (from live tracking) */
  currentLongitude?: number;
  /** HyperTrack embedded map URL for live tracking view */
  trackingUrl?: string;
}

const queryParamsSchema = z.object({
  "filter[trip.id]": z.string(),
});

interface TripIdentifier {
  shiftId: string;
  workerId: string;
}

interface UseGetWorkerTripsParams {
  workplaceId: string;
  tripIds: TripIdentifier[];
}

interface UseGetWorkerTripsOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

function buildTripsUrl(workplaceId: string): string {
  return `${environmentConfig.REACT_APP_API_MAIN_URL}/api/workplaces/${workplaceId}/trips`;
}

function buildFilterValue(tripIds: TripIdentifier[]): string {
  return tripIds.map(({ shiftId, workerId }) => `${shiftId}-${workerId}`).join(",");
}

function transformToWorkerTripData(trip: TripResponseItem): WorkerTripData {
  const { attributes } = trip;

  return {
    shiftId: attributes.shiftId,
    workerId: attributes.workerId,
    isLate: attributes.isLate,
    status: attributes.status,
    currentEtaDuration: attributes.currentEtaDuration,
    currentEtaDistance: attributes.currentEtaDistance,
    latestWorkerMovement: attributes.latestWorkerMovement,
    latestWorkerMovementTransitionedAt: attributes.latestWorkerMovementTransitionedAt,
    lastLocationReceivedByServerAt: attributes.lastLocationReceivedByServerAt,
    workerDeclaredEta: attributes.workerDeclaredEta,
    currentLatitude: attributes.currentLatitude,
    currentLongitude: attributes.currentLongitude,
    trackingUrl: attributes.trackingUrl,
  };
}

/**
 * Fetches live trip data for multiple workers on active shifts.
 * Polls every 60 seconds when enabled. Returns a map of shiftId to trip data
 * for efficient lookup when rendering shift cards.
 *
 * Trip IDs use the format `{shiftId}-{workerId}` matching the backend filter API.
 */
export function useGetWorkerTrips(
  params: UseGetWorkerTripsParams,
  options: UseGetWorkerTripsOptions = {}
) {
  const { workplaceId, tripIds } = params;
  const { enabled = true, refetchInterval = WORKER_TRIPS_REFETCH_INTERVAL_MS } = options;

  const filterValue = buildFilterValue(tripIds);
  const url = buildTripsUrl(workplaceId);

  const query = useQuery({
    queryKey: [url, filterValue],
    queryFn: async () => {
      const response = await get({
        url,
        queryParams: { "filter[trip.id]": filterValue },
        queryParamsSchema,
        responseSchema: tripsResponseSchema,
      });

      return response.data;
    },
    enabled: enabled && tripIds.length > 0 && Boolean(workplaceId),
    refetchInterval,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_TRIPS_FAILURE,
    },
  });

  const tripsByShiftId = useMemo(() => {
    const map = new Map<string, WorkerTripData>();
    if (!query.data) {
      return map;
    }

    for (const trip of query.data) {
      map.set(trip.attributes.shiftId, transformToWorkerTripData(trip));
    }

    return map;
  }, [query.data]);

  return {
    isLoading: query.isLoading,
    tripsByShiftId,
  };
}

export const WORKER_TRIPS_QUERY_KEY_PREFIX = ["worker-trips"] as const;
